/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file NotificationBellButton.js
 * @description
 * The one notification entry point used across the app: a themed bell button with an
 * unread badge that opens {@link module:screens/NotificationsScreen}. Dropped into the
 * management `TopBar` (so every management screen has it) and the POS/online
 * `CustomHeader`, so the inbox is reachable from ANY module instead of only from the
 * handful of screens that happened to hard-code a bell.
 *
 * The badge number comes from the shared `helpers/appBadge` store — the very same
 * fetch that paints the OS app-icon badge — so opening a screen costs no extra
 * request and the two badges can never disagree. A `pending` prop lets the POS header
 * keep counting its pending orders (which the inbox pins above the list) on top.
 */
import React, {useCallback, useEffect, useState} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import SafeIcon from './management/SafeIcon';
import {refreshAppBadge, subscribeAppBadgeCounts} from '../helpers/appBadge';

/** Anything above this renders as "99+" — a 3-digit badge breaks the pill. */
const BADGE_DISPLAY_CAP = 99;

/**
 * Bell button with an unread badge.
 *
 * @param {object} props
 * @param {number} [props.size=22]        Glyph size.
 * @param {string} [props.color]          Glyph colour (defaults to the caller's text colour).
 * @param {number} [props.pending=0]      Extra count to add (e.g. pending POS orders).
 * @param {object} [props.style]          Style for the touchable.
 * @param {object} [props.badgeStyle]     Style override for the badge pill.
 * @param {boolean} [props.hideWhenEmpty] Render nothing at all when the count is 0.
 * @returns {?JSX.Element}
 */
const NotificationBellButton = ({
    size = 22,
    color,
    pending = 0,
    style,
    badgeStyle,
    hideWhenEmpty = false,
}) => {
    const navigation = useNavigation();
    const {t} = useTranslation();
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        // Fires immediately with the cached counters, then on every refresh.
        const unsubscribe = subscribeAppBadgeCounts((counts) => {
            setUnread(Number(counts?.notifications) || 0);
        });
        // Opening a screen that shows the bell is a good moment to re-read; the
        // helper coalesces this with whatever else asked for a refresh.
        refreshAppBadge();

        return unsubscribe;
    }, []);

    const total = unread + (Number(pending) || 0);

    const onPress = useCallback(() => {
        navigation.navigate('NotificationsScreen');
    }, [navigation]);

    if (hideWhenEmpty && total === 0) {
        return null;
    }

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.7}
            hitSlop={12}
            accessibilityRole="button"
            accessibilityLabel={t('notifications_screen_title', {defaultValue: 'Értesítések'})}
            style={style}
        >
            <View style={styles.wrap}>
                <SafeIcon name="bell-outline" fallback="bell" size={size} color={color} />
                {total > 0 && (
                    <View style={[styles.badge, badgeStyle]}>
                        <Text style={styles.badgeText} numberOfLines={1}>
                            {total > BADGE_DISPLAY_CAP ? `${BADGE_DISPLAY_CAP}+` : total}
                        </Text>
                    </View>
                )}
            </View>
        </TouchableOpacity>
    );
};

const styles = {
    wrap: {position: 'relative'},
    badge: {
        position: 'absolute',
        top: -6,
        right: -8,
        minWidth: 18,
        height: 18,
        paddingHorizontal: 4,
        borderRadius: 9,
        backgroundColor: '#EF4444',
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeText: {
        color: '#FFFFFF',
        fontSize: 10.5,
        fontWeight: '800',
        lineHeight: 14,
    },
};

export default React.memo(NotificationBellButton);
